/**
 * Contentful API client
 *
 * This file provides functions for fetching content from Contentful.
 * It handles the case where the Contentful client might not be initialized.
 */

import { contentfulClient } from './config';
import { BlogPost } from '../types/contentful';

/**
 * Calculate similarity between two strings (Levenshtein distance)
 * @param str1 - First string
 * @param str2 - Second string
 * @returns Similarity score between 0 and 1 (1 being identical)
 */
function calculateSimilarity(str1: string, str2: string): number {
  // If either string is empty, return 0
  if (!str1 || !str2) return 0;

  // If strings are identical, return 1
  if (str1 === str2) return 1;

  // If one string contains the other, return a high score
  if (str1.includes(str2) || str2.includes(str1)) {
    return 0.9;
  }

  // Calculate Levenshtein distance
  const track = Array(str2.length + 1).fill(null).map(() =>
    Array(str1.length + 1).fill(null));

  for (let i = 0; i <= str1.length; i += 1) {
    track[0][i] = i;
  }

  for (let j = 0; j <= str2.length; j += 1) {
    track[j][0] = j;
  }

  for (let j = 1; j <= str2.length; j += 1) {
    for (let i = 1; i <= str1.length; i += 1) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      track[j][i] = Math.min(
        track[j][i - 1] + 1, // deletion
        track[j - 1][i] + 1, // insertion
        track[j - 1][i - 1] + indicator, // substitution
      );
    }
  }

  // Calculate similarity as 1 - normalized distance
  const maxLength = Math.max(str1.length, str2.length);
  const distance = track[str2.length][str1.length];
  return 1 - distance / maxLength;
}

/**
 * Get all blog posts from Contentful
 * @returns Promise<BlogPost[]> - Array of blog posts
 */
export async function getAllBlogPosts(): Promise<BlogPost[]> {
  console.log('getAllBlogPosts called, contentfulClient:', contentfulClient ? 'available' : 'not available');

  // Check if contentfulClient is available
  if (!contentfulClient) {
    console.warn('Contentful client not initialized. Returning empty blog posts array.');
    return [];
  }

  try {
    console.log('Fetching blog posts from Contentful...');
    // Fetch blog posts from Contentful
    const response = await contentfulClient.getEntries({
      content_type: 'blogPost',
      order: ['-sys.createdAt'] as any,
    });

    console.log(`Found ${response.items.length} blog posts`);
    // Return blog posts
    return response.items as unknown as BlogPost[];
  } catch (error) {
    console.error('Error fetching blog posts from Contentful:', error);
    return [];
  }
}

/**
 * Get a blog post by slug
 * @param slug - Blog post slug
 * @returns Promise<BlogPost | null> - Blog post or null if not found
 */
export async function getBlogPostBySlug(slug: string): Promise<BlogPost | null> {
  // Check if contentfulClient is available
  if (!contentfulClient) {
    console.warn('Contentful client not initialized. Cannot fetch blog post by slug.');
    return null;
  }

  try {
    console.log(`Fetching blog post with slug: ${slug}`);

    // Try exact match first
    let response = await contentfulClient.getEntries({
      content_type: 'blogPost',
      'fields.seoPath': slug,
      limit: 1,
    });

    // If no results, try with a more flexible approach
    if (response.items.length === 0) {
      console.log(`No exact match found for slug: ${slug}, trying alternative approaches`);

      // Get all blog posts and filter manually
      const allPostsResponse = await contentfulClient.getEntries({
        content_type: 'blogPost',
        limit: 100, // Adjust based on your content volume
      });

      // Log all available seoPath values for debugging
      const availablePaths = allPostsResponse.items.map((item: any) => item.fields.seoPath);
      console.log('Available seoPath values:', availablePaths);

      // Log the closest matches for better debugging
      const closestMatches = availablePaths
        .map(path => ({ path, similarity: calculateSimilarity(slug, path) }))
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, 3);

      console.log('Closest matches:', closestMatches);

      // Try to find a post with the best match
      // First, try exact match
      let matchingPost = allPostsResponse.items.find((item: any) => {
        return item.fields.seoPath === slug;
      });

      // If no exact match, try partial match
      if (!matchingPost) {
        matchingPost = allPostsResponse.items.find((item: any) => {
          const itemPath = item.fields.seoPath;
          return slug.includes(itemPath) || itemPath.includes(slug);
        });
      }

      // If still no match, use the closest match based on similarity
      if (!matchingPost && closestMatches.length > 0 && closestMatches[0].similarity > 0.7) {
        const closestPath = closestMatches[0].path;
        console.log(`Using closest match: ${closestPath} (similarity: ${closestMatches[0].similarity})`);
        matchingPost = allPostsResponse.items.find((item: any) => {
          return item.fields.seoPath === closestPath;
        });
      }

      if (matchingPost) {
        console.log(`Found matching post with seoPath: ${matchingPost.fields.seoPath}`);
        return matchingPost as unknown as BlogPost;
      }

      console.warn(`No blog post found with slug: ${slug}`);
      return null;
    }

    console.log(`Found blog post with slug: ${slug}`);
    return response.items[0] as unknown as BlogPost;
  } catch (error) {
    console.error('Error fetching blog post by slug from Contentful:', error);
    return null;
  }
}

/**
 * Get blog posts by category
 * @param category - Blog post category
 * @returns Promise<BlogPost[]> - Array of blog posts
 */
export async function getBlogPostsByCategory(category: string): Promise<BlogPost[]> {
  // Check if contentfulClient is available
  if (!contentfulClient) {
    console.warn('Contentful client not initialized. Returning empty blog posts array.');
    return [];
  }

  try {
    // Fetch blog posts from Contentful
    const response = await contentfulClient.getEntries({
      content_type: 'blogPost',
      'fields.category': category,
      order: ['-sys.createdAt'] as any,
    });

    // Return blog posts
    return response.items as unknown as BlogPost[];
  } catch (error) {
    console.error('Error fetching blog posts by category from Contentful:', error);
    return [];
  }
}